import React from 'react';
import { User as UserIcon, Mail, Building2, Shield, Calendar, Edit2, Trash2, Clock } from 'lucide-react';

interface UserCardProps {
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
    company?: string;
    createdBy: string;
    date: string;
    userType?: string;
    empresa?: { name: string };
    createdAt?: string;
    empresaId?: number;
    companyId?: number;
  };
  onEdit: (user: any) => void;
  onDelete: (user: any) => void;
  canManage: boolean;
  getRoleDisplayName: (role: string) => string;
}

const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case 'ADMIN':
      return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'ANALYST':
      return 'bg-sky-500/20 text-sky-300 border-sky-500/30';
    case 'CLIENT':
      return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    default:
      return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  }
};

const formatRelativeTime = (dateString?: string) => {
  if (!dateString) return 'Fecha desconocida';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins}m`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 30) return `Hace ${diffDays}d`;
  return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onDelete,
  canManage,
  getRoleDisplayName
}) => {
  const isInternal = user.userType?.toUpperCase() === 'INTERNAL';
  const companyName = user.empresa?.name || user.company || '—';

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg transition-all duration-200 hover:border-sky-500/50 hover:shadow-lg hover:shadow-sky-500/10">
      {/* Header */}
      <div className="p-5 border-b border-slate-700">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-lg flex-shrink-0 ${isInternal ? 'bg-sky-500/20' : 'bg-blue-500/20'}`}>
            <UserIcon className={`w-5 h-5 ${isInternal ? 'text-sky-400' : 'text-blue-400'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate mb-1">
              {user.name}
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getRoleBadgeColor(user.role)}`}>
                {getRoleDisplayName(user.role)}
              </span>
              <span className={`px-2.5 py-0.5 text-xs font-medium rounded ${isInternal ? 'bg-sky-700 text-sky-200' : 'bg-slate-700 text-slate-300'}`}>
                {isInternal ? 'Interno' : 'Externo'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Body - Metadata */}
      <div className="p-5 space-y-3">
        {/* Email */}
        <div className="flex items-start gap-3 text-sm">
          <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-slate-400 text-xs mb-1">Email</p>
            <p className="text-slate-200 text-sm truncate">{user.email}</p>
          </div>
        </div>

        {/* Empresa */}
        <div className="flex items-start gap-3 text-sm">
          <Building2 className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-slate-400 text-xs mb-1">Empresa</p>
            <p className="text-slate-200 text-sm truncate">{companyName}</p>
          </div>
        </div>

        {/* Creado por */}
        {user.createdBy && user.createdBy !== '—' && (
          <div className="flex items-start gap-3 text-sm">
            <Shield className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-slate-400 text-xs mb-1">Creado por</p>
              <p className="text-slate-200 text-sm truncate">{user.createdBy}</p>
            </div>
          </div>
        )}

        {/* Metadata Footer */}
        <div className="flex items-center gap-4 pt-2 border-t border-slate-700 text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>Creado {formatRelativeTime(user.createdAt || user.date)}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      {canManage && (
        <div className="p-4 bg-slate-900/50 border-t border-slate-700 flex items-center justify-end gap-2">
          <button
            onClick={() => onEdit(user)}
            className="
              flex items-center gap-2 px-4 py-2 text-sm font-medium
              bg-blue-600 hover:bg-blue-700 text-white rounded-lg
              transition-colors
            "
          >
            <Edit2 className="w-4 h-4" />
            Editar
          </button>
          <button
            onClick={() => onDelete(user)}
            className="
              flex items-center gap-2 px-4 py-2 text-sm font-medium
              bg-red-600 hover:bg-red-700 text-white rounded-lg
              transition-colors
            "
          >
            <Trash2 className="w-4 h-4" />
            Eliminar
          </button>
        </div>
      )}
    </div>
  );
};
